import { isEqual } from 'lodash-es';
import { FilterType } from '@/models/filter/FilterType';

type FilterValueRecord = Record<string, unknown>;

const getObjectValue = (values: FilterValueRecord, key: string): unknown =>
  Object.getOwnPropertyDescriptor(values, key)?.value;

const getObjectValueWithDefault = (
  values: FilterValueRecord,
  key: string,
  defaultValues?: FilterValueRecord,
): unknown => {
  const value = getObjectValue(values, key);
  if (value !== undefined || !defaultValues) return value;
  return getObjectValue(defaultValues, key);
};

export const getFilterStateKeys = (...states: (FilterValueRecord | null | undefined)[]): string[] => {
  const keys = new Set<string>();

  for (const state of states) {
    if (state) {
      for (const key of Object.keys(state)) {
        keys.add(key);
      }
    }
  }

  return Array.from(keys);
};

export const getComparableFilterKeys = (
  configuredKeys: readonly FilterType[] | undefined,
  currentValues: FilterValueRecord,
  baselineValues: FilterValueRecord,
  defaultValues?: FilterValueRecord,
): readonly string[] => {
  if (configuredKeys?.length) return configuredKeys;
  return getFilterStateKeys(currentValues, baselineValues, defaultValues);
};

export const hasFilterValueChanges = (
  filterKeys: readonly string[],
  currentValues: FilterValueRecord,
  baselineValues: FilterValueRecord,
  defaultValues?: FilterValueRecord,
): boolean =>
  filterKeys.some(
    filterKey =>
      !isEqual(
        getObjectValueWithDefault(currentValues, filterKey, defaultValues),
        getObjectValueWithDefault(baselineValues, filterKey, defaultValues),
      ),
  );

export const isFieldEmpty = (value: unknown): boolean => {
  if (value == null) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  switch (typeof value) {
    case 'string':
      return /^\s*$/.test(value);

    case 'boolean':
      return !value;

    case 'number':
      return value === 0;

    default:
      return false;
  }
};
